import { useContext, useEffect, useState } from 'react';
import {
  Backdrop,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Fade,
  Grid,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { BarChart, LineChart, PieChart } from '@mui/x-charts';
import { DashboardContext } from 'contexts/DashboardContext';
import { DropsContext } from 'contexts/DropsContext';
import { AuthContext } from 'contexts/AuthContext';
import { findOnArray } from 'utils/functions';
import { dropMeses } from 'utils/drops';
import Container from 'components/Container';
import Dropdown from 'components/Dropdown';
import Button from 'components/Button';
import Input from 'components/Input';
import moment from 'moment';
import styles from './styles';

const App = () => {
  const theme = useTheme();
  const [type, setType] = useState('valor');
  const { user } = useContext(AuthContext);
  const { drops } = useContext(DropsContext);
  const { dashData, getLoading, getDashboard } = useContext(DashboardContext);
  const defaultValues = {
    filial_id: null,
    mes: moment().format('M'),
    ano: moment().format('Y'),
  };
  const { control, handleSubmit, watch } = useForm({ defaultValues });

  const mes = watch('mes');
  const ano = watch('ano');
  const filial_id = watch('filial_id');

  const loadData = (params) => getDashboard({ params });

  const generateSubtitle = () => {
    let str = 'Dados';
    if (Boolean(filial_id)) {
      const filial = findOnArray(filial_id, drops?.Filial, 'label');
      str += ` da filial ${filial}`;
    }
    if (Boolean(mes) && Boolean(ano)) {
      const dt = moment(mes + '-' + ano, 'M-Y');
      const inicio = dt.startOf('month').format('DD/MM/YYYY');
      const fim = dt.endOf('month').format('DD/MM/YYYY');
      if (Boolean(filial_id)) {
        str += ' no';
      } else {
        str += ' do';
      }
      str += ` período de ${inicio} até ${fim}`;
    }

    return str;
  };

  const pieColor = {
    PENDENTE: theme?.palette?.warning?.main,
    CONCLUIDO: theme?.palette?.success?.main,
    CANCELADO: theme?.palette?.error?.main,
  };

  const pieCharts = [
    {
      title: 'Orçamentos',
      data: dashData?.orçamentos,
      show: !user?.permissoes?.block_dashboard_orcamentos,
    },
    {
      title: 'Pedidos',
      data: dashData?.pedidos,
      show: !user?.permissoes?.block_dashboard_pedidos,
    },
    {
      title: 'Faturas',
      data: dashData?.faturas,
      show: !user?.permissoes?.block_dashboard_faturas,
    },
  ];

  const pieSeries = (arr) => [
    {
      data: arr.map((d) => ({ ...d, color: pieColor[d.label] })),
      innerRadius: 40,
      outerRadius: 80,
      paddingAngle: 4,
      cornerRadius: 5,
    },
  ];

  const lineSeries = (arr) => [
    {
      showMark: false,
      curve: 'monotone',
      label: 'Faturamento (R$)',
      color: theme?.palette?.primary?.main,
      data: arr?.map((item) => item?.valor || 0),
      valueFormatter: (v) =>
        v?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
    },
  ];

  const lineXAxis = (arr) => [
    {
      scaleType: 'band',
      data: arr?.map((item) => item?.dia),
      valueFormatter: (v) =>
        `${String(v)?.padStart(2, '0')}${mes ? `/${moment(mes, 'M').format('MM')}` : ''}`,
    },
  ];

  const lineYAxis = [
    {
      valueFormatter: (v) =>
        v?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
    },
  ];

  const barSeries = [
    {
      dataKey: type,
      label: type === 'quantidade' ? 'Quantidade' : 'Valor (R$)',
      color: theme.palette.primary.main,
      valueFormatter: (v) =>
        type === 'quantidade'
          ? v
          : v?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
    },
  ];

  const barXAxis = [
    {
      scaleType: 'band',
      dataKey: 'nome',
      tickLabelStyle: { fontSize: 10, whiteSpace: 'normal' },
      categoryGapRatio: 0.75,
    },
  ];

  const barYAxis = [
    {
      valueFormatter: (v) =>
        type === 'quantidade'
          ? v
          : v?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
    },
  ];

  useEffect(() => {
    handleSubmit(loadData)();
  }, []);

  return (
    <Container>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box component="form" onSubmit={handleSubmit(loadData)}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Dropdown
                  name="filial_id"
                  control={control}
                  label="Filial"
                  options={drops?.Filial}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Dropdown
                  name="mes"
                  label="Mês"
                  control={control}
                  options={dropMeses}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Input type="number" name="ano" control={control} label="Ano" />
              </Grid>
              <Grid item xs={12} sm="auto" display="flex" alignItems="center">
                <Button
                  type="submit"
                  sx={styles?.button}
                  variant="contained"
                  onClick={handleSubmit(loadData)}
                >
                  Aplicar Filtro
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="text.secondary">
            {generateSubtitle()}
          </Typography>
        </Grid>
        {pieCharts?.map(
          ({ title, data = [], show }, idx) =>
            show && (
              <Grid item xs={12} lg={4} key={idx}>
                <Card sx={styles.card}>
                  <CardContent>
                    <Typography variant="h6">{title}</Typography>
                    {Boolean(data?.length) ? (
                      <PieChart
                        height={250}
                        series={pieSeries(data)}
                        margin={styles?.pieMargin}
                        slotProps={styles?.pieSlot}
                      />
                    ) : (
                      <Typography textAlign="center" variant="body2">
                        Nenhum registro encontrado
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )
        )}
        {!user?.permissoes?.block_dashboard_faturamentos && (
          <Grid item xs={12}>
            <Card sx={styles?.card}>
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography variant="h6">Faturamento por vendedor</Typography>
                  <Select
                    size="small"
                    value={type}
                    onChange={(e) => setType(e?.target?.value)}
                  >
                    <MenuItem value="quantidade">QUANTIDADE</MenuItem>
                    <MenuItem value="valor">VALOR</MenuItem>
                  </Select>
                </Box>
                {Boolean(dashData?.faturamentos?.length) ? (
                  <BarChart
                    height={300}
                    xAxis={barXAxis}
                    yAxis={barYAxis}
                    series={barSeries}
                    margin={styles?.lineMargin}
                    slotProps={styles?.lineSlot}
                    dataset={dashData?.faturamentos}
                  />
                ) : (
                  <Typography textAlign="center" variant="body2">
                    Nenhum registro encontrado
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
        {!user?.permissoes?.block_dashboard_faturamentos_dia && (
          <Grid item xs={12}>
            <Card sx={styles?.card}>
              <CardContent>
                <Typography variant="h6">Faturamento diário</Typography>
                {Boolean(dashData?.faturamentos_dia?.length) ? (
                  <LineChart
                    height={300}
                    yAxis={lineYAxis}
                    margin={styles?.lineMargin}
                    slotProps={styles?.lineSlot}
                    xAxis={lineXAxis(dashData?.faturamentos_dia)}
                    series={lineSeries(dashData?.faturamentos_dia)}
                  />
                ) : (
                  <Typography textAlign="center" variant="body2">
                    Nenhum registro encontrado
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
      <Fade in={getLoading}>
        <Backdrop open={getLoading} sx={styles?.loader}>
          <CircularProgress color="inherit" size={48} thickness={4} />
        </Backdrop>
      </Fade>
    </Container>
  );
};

export default App;
